/**
 * SDK de tokenização de cartão do PicPay.
 *
 * Location: src/lib
 * Why: o número do cartão **nunca** pode chegar a um servidor da Hystore. O
 * SDK do PicPay converte o cartão num token de uso único aqui no navegador, e
 * só esse token viaja até a nossa API — é o que mantém a Hystore fora do
 * escopo PCI-DSS.
 *
 * Carregado sob demanda, não no bundle: quem paga por PIX ou pela carteira não
 * baixa um script de terceiro à toa.
 *
 * Referência: https://developers-business.picpay.com/checkout/docs/transparent-checkout/sdk
 */

const CDN = 'https://checkout.picpay.com/cdn/pp-transparent-v1.0.0.js';

/** O token vale 5 minutos. Vale para uma tentativa de pagamento, não mais. */
export const VALIDADE_TOKEN_MS = 5 * 60 * 1000;

export interface DadosCartao {
  numero: string;
  nome: string;
  documento: string;
  mesValidade: string;
  anoValidade: string;
  cvv: string;
}

export interface CredenciaisTokenizacao {
  merchantCredential: string;
  transparentToken: string;
}

interface SdkPicPay {
  setCredentials(c: CredenciaisTokenizacao): void;
  getCardBrand(a: {
    bin: string;
    success: (r: { brand: string }) => void;
    error: (r: { errors?: Array<{ message?: string }> }) => void;
  }): void;
  createTemporaryCard(a: {
    card: Record<string, string>;
    success: (r: { temporaryToken: string }) => void;
    error: (r: { errors?: Array<{ message?: string }> }) => void;
  }): void;
}

declare global {
  interface Window {
    CheckoutTransparent?: SdkPicPay;
  }
}

let carregando: Promise<SdkPicPay> | null = null;

/**
 * Carrega o script uma vez só.
 *
 * A promessa guardada faz duas chamadas simultâneas esperarem o mesmo
 * carregamento, em vez de inserir duas tags iguais no documento.
 */
export function carregarSdkPicPay(): Promise<SdkPicPay> {
  if (window.CheckoutTransparent) return Promise.resolve(window.CheckoutTransparent);
  if (carregando) return carregando;

  carregando = new Promise<SdkPicPay>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CDN;
    script.async = true;

    script.onload = () => {
      if (window.CheckoutTransparent) {
        resolve(window.CheckoutTransparent);
      } else {
        reject(new Error('SDK do PicPay carregou sem expor CheckoutTransparent'));
      }
    };

    script.onerror = () => {
      // Zera para uma próxima tentativa poder recarregar: uma falha de rede
      // não pode deixar o cartão indisponível pelo resto da sessão.
      carregando = null;
      reject(new Error('Não foi possível carregar o SDK do PicPay'));
    };

    document.head.appendChild(script);
  });

  return carregando;
}

function soDigitos(v: string): string {
  return v.replace(/\D/g, '');
}

/** Erro do SDK vira uma frase; o formato deles é uma lista de objetos. */
function primeiraMensagem(r: { errors?: Array<{ message?: string }> }): string {
  return r.errors?.[0]?.message ?? 'Não foi possível validar o cartão.';
}

/**
 * Descobre a bandeira pelos 6 primeiros dígitos.
 *
 * O `createTemporaryCard` exige a bandeira, então esta chamada vem antes. Os
 * 6 dígitos (BIN) não identificam o cartão nem o portador — é a faixa do
 * emissor.
 */
export function buscarBandeira(sdk: SdkPicPay, numero: string): Promise<string> {
  return new Promise((resolve, reject) => {
    sdk.getCardBrand({
      bin: soDigitos(numero).slice(0, 6),
      success: (r) => resolve(r.brand),
      error: (r) => reject(new Error(primeiraMensagem(r))),
    });
  });
}

/**
 * Transforma o cartão num token de uso único.
 *
 * O objeto `card` existe só dentro desta função e do SDK. Nada aqui é enviado
 * para a nossa API, guardado em estado global ou registrado em log.
 */
export function tokenizarCartao(
  sdk: SdkPicPay,
  cartao: DadosCartao,
  bandeira: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    sdk.createTemporaryCard({
      card: {
        brand: bandeira,
        number: soDigitos(cartao.numero),
        holderName: cartao.nome.trim().toUpperCase(),
        holderDocument: soDigitos(cartao.documento),
        expirationMonth: cartao.mesValidade.padStart(2, '0'),
        expirationYear: cartao.anoValidade,
        cvv: soDigitos(cartao.cvv),
      },
      success: (r) => resolve(r.temporaryToken),
      error: (r) => reject(new Error(primeiraMensagem(r))),
    });
  });
}

/**
 * O caminho completo: carrega, autentica, descobre a bandeira, tokeniza.
 *
 * Uma função só porque os quatro passos não fazem sentido separados — quem
 * chama quer um token, não uma sequência.
 */
export async function obterTokenDoCartao(
  credenciais: CredenciaisTokenizacao,
  cartao: DadosCartao,
): Promise<string> {
  const sdk = await carregarSdkPicPay();

  sdk.setCredentials(credenciais);

  const bandeira = await buscarBandeira(sdk, cartao.numero);

  return tokenizarCartao(sdk, cartao, bandeira);
}
